import { supabase } from '../supabaseClient';
import type { ClassMemberGradeLayout, ClassMemberGroupMeta } from '../utils/classMembersStorage';
import { recordDevLocalStorageProgramCacheUse } from '../utils/devNetworkTransferMonitor';
import {
  cacheClassMemberGradeLayout,
  ClassMemberRow,
  DEFAULT_MEMBER_PROFILE_IMAGE,
  emptyGradeLayout,
  flattenGradeLayout,
  hasClassMemberLocalStorageSnapshot,
  loadClassMembers,
  loadClassMemberGradeLayoutFromStorage,
  normalizeEntryNumber,
  normalizeMemberGroupNo,
  normalizeProfileImageId,
  normalizeSeatPosition,
} from '../utils/classMembersStorage';
import {
  allowSchoolScopedCacheSeedAfterInvalidate,
  invalidateSchoolScopedDataCaches,
  notifySchoolProgramMutation,
} from '../utils/schoolDataCacheVersion';
import { purgeMemberOwnedSoftRefs } from './classMemberOwnedDataService';
import { normalizeMemberStudentNumber, sortMembersByDisplayNumber } from '../utils/classMemberNumbers';

type ClassMemberRecord = {
  id: string;
  workspace_id: string;
  grade_id: string;
  name: string;
  gender: '' | '남' | '여';
  sort_order: number;
  member_group_id: string | null;
  seat_position?: unknown;
  profile_image?: string | null;
  photo_profile_path?: string | null;
  photo_profile_url?: string | null;
  photo_profile_version?: number | null;
  entry_number?: string | null;
  group_no?: number | null;
  student_number?: number | null;
};

let warnedMissingStudentNumberColumn = false;
/** DB에 student_number 컬럼(마이그레이션)이 아직 없을 때: 순서 번호로 동작하고 경고만 한 번 남긴다. */
function warnMissingStudentNumberColumnOnce(): void {
  if (warnedMissingStudentNumberColumn) return;
  warnedMissingStudentNumberColumn = true;
  // eslint-disable-next-line no-console
  console.warn(
    '[classMembers] class_members.student_number 컬럼이 아직 없습니다(마이그레이션 미적용). 순서 기반 번호로 표시합니다.'
  );
}

function toMemberRow(row: ClassMemberRecord, cached?: ClassMemberRow): ClassMemberRow {
  const seatPosition = normalizeSeatPosition(row.seat_position);
  const photoPath = (row.photo_profile_path ?? '').trim();
  const photoUrl = (row.photo_profile_url ?? '').trim();
  const photoVer = Number(row.photo_profile_version);
  const groupNo =
    'group_no' in row
      ? normalizeMemberGroupNo(row.group_no)
      : (cached?.groupNo ?? null);
  let studentNumber: number | null;
  if ('student_number' in row) {
    studentNumber = normalizeMemberStudentNumber(row.student_number);
  } else {
    warnMissingStudentNumberColumnOnce();
    studentNumber = normalizeMemberStudentNumber(cached?.studentNumber);
  }
  return {
    id: row.id,
    name: row.name ?? '',
    gender: row.gender === '남' || row.gender === '여' ? row.gender : '',
    groupNo,
    ...(studentNumber != null ? { studentNumber } : {}),
    entryNumber: normalizeEntryNumber(row.entry_number),
    profileImage: normalizeProfileImageId(row.profile_image),
    ...(seatPosition ? { seatPosition } : {}),
    ...(photoPath ? { photoProfilePath: photoPath } : {}),
    ...(photoUrl ? { photoProfileUrl: photoUrl } : {}),
    ...(Number.isFinite(photoVer) && photoVer > 0
      ? { photoProfileVersion: Math.trunc(photoVer) }
      : {}),
    ...(cached?.nameChoseongPositions?.length
      ? { nameChoseongPositions: cached.nameChoseongPositions }
      : {}),
  };
}

async function fetchGroupsForGrade(
  workspaceId: string,
  gradeId: string
): Promise<Array<{ id: string; name: string; sort_order: number }>> {
  const { data, error } = await supabase
    .from('class_member_groups')
    .select('id, name, sort_order')
    .eq('workspace_id', workspaceId)
    .eq('grade_id', gradeId)
    .order('sort_order', { ascending: true })
    .order('created_at', { ascending: true });
  if (error) throw new Error(error.message);
  return (data ?? []) as Array<{ id: string; name: string; sort_order: number }>;
}

/**
 * 멤버 이름은 DB에 pgp_sym으로 암호화 저장되어 있어(class_members.name_enc),
 * 평문 name은 항상 빈 값이다. class_members_list RPC(SECURITY DEFINER)가
 * 내부에서 복호화해 기존과 동일한 shape의 row를 반환한다 — 네트워크 왕복 수는 그대로.
 */
async function fetchMembersForGrade(workspaceId: string, gradeId: string): Promise<ClassMemberRecord[]> {
  const { data, error } = await supabase.rpc('class_members_list', {
    p_workspace_id: workspaceId,
    p_grade_id: gradeId,
  });
  if (error) throw new Error(error.message);
  return (data as unknown as ClassMemberRecord[]) ?? [];
}

function cachedRowById(cached: ClassMemberGradeLayout | null | undefined): Map<string, ClassMemberRow> {
  const map = new Map<string, ClassMemberRow>();
  if (!cached) return map;
  for (const list of Object.values(cached.membersByGroupId)) {
    for (const row of list) map.set(row.id, row);
  }
  return map;
}

function buildLayoutFromDb(
  groups: Array<{ id: string; name: string; sort_order: number }>,
  members: ClassMemberRecord[],
  cached?: ClassMemberGradeLayout | null
): ClassMemberGradeLayout {
  const groupMetas: ClassMemberGroupMeta[] = groups.map((g) => ({
    id: g.id,
    name: g.name ?? '',
    sortOrder: g.sort_order,
  }));
  const byGroup = new Map<string, ClassMemberRecord[]>();
  for (const g of groups) {
    byGroup.set(g.id, []);
  }
  const orphaned: ClassMemberRecord[] = [];
  for (const m of members) {
    const gid = m.member_group_id;
    if (gid && byGroup.has(gid)) {
      byGroup.get(gid)!.push(m);
    } else {
      orphaned.push(m);
    }
  }
  const firstId = groups[0]?.id;
  if (firstId && orphaned.length) {
    byGroup.get(firstId)!.push(...orphaned);
  }
  const membersByGroupId: Record<string, ClassMemberRow[]> = {};
  for (const g of groups) {
    const list = (byGroup.get(g.id) ?? []).sort((a, b) => a.sort_order - b.sort_order || a.id.localeCompare(b.id));
    const byId = cachedRowById(cached);
    // 고정 번호가 있는 그룹은 번호순(없는 멤버는 뒤로), 없으면 sort_order 순 그대로
    membersByGroupId[g.id] = sortMembersByDisplayNumber(list.map((m) => toMemberRow(m, byId.get(m.id))));
  }
  return { groups: groupMetas, membersByGroupId };
}

async function createDefaultGroupRow(workspaceId: string, gradeId: string): Promise<string> {
  const id = `cmg-${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
  const { error } = await supabase.from('class_member_groups').insert({
    id,
    workspace_id: workspaceId,
    grade_id: gradeId,
    name: '기본',
    sort_order: 0,
    updated_at: new Date().toISOString(),
  });
  if (error) throw new Error(error.message);
  return id;
}

async function loadLayoutFromDb(workspaceId: string, gradeId: string): Promise<ClassMemberGradeLayout> {
  const cached = loadClassMemberGradeLayoutFromStorage(workspaceId, gradeId);
  const [groups, members] = await Promise.all([
    fetchGroupsForGrade(workspaceId, gradeId),
    fetchMembersForGrade(workspaceId, gradeId),
  ]);
  if (groups.length === 0 && members.length === 0) {
    return emptyGradeLayout();
  }
  const byId = cachedRowById(cached);
  if (groups.length === 0 && members.length > 0) {
    const gid = await createDefaultGroupRow(workspaceId, gradeId);
    const layout: ClassMemberGradeLayout = {
      groups: [{ id: gid, name: '기본', sortOrder: 0 }],
      membersByGroupId: { [gid]: members.map((m) => toMemberRow(m, byId.get(m.id))) },
    };
    await replaceGradeMemberLayout(workspaceId, gradeId, layout);
    return layout;
  }
  return buildLayoutFromDb(groups, members, cached);
}

/** 동일 학년 레이아웃 동시 요청(StrictMode·중복 이펙트)을 한 번의 DB 조회로 합침 */
const pendingGradeLayouts = new Map<string, Promise<ClassMemberGradeLayout>>();

async function loadGradeLayoutCached(workspaceId: string, gradeId: string): Promise<ClassMemberGradeLayout> {
  const k = `${workspaceId}|${gradeId}`;
  const existing = pendingGradeLayouts.get(k);
  if (existing) return existing;
  const task = loadLayoutFromDb(workspaceId, gradeId)
    .then((layout) => {
      cacheClassMemberGradeLayout(workspaceId, gradeId, layout);
      return layout;
    })
    .finally(() => {
      pendingGradeLayouts.delete(k);
    });
  pendingGradeLayouts.set(k, task);
  return task;
}

export type GetClassMemberGradeLayoutsMapOptions = {
  forceRefresh?: boolean;
  /** true면 LS만 사용 (메인 프레젠 — REST 금지) */
  cacheOnly?: boolean;
};

export async function getClassMemberGradeLayoutsMap(
  workspaceId: string,
  gradeIds: string[],
  options?: GetClassMemberGradeLayoutsMapOptions
): Promise<Record<string, ClassMemberGradeLayout>> {
  const uniqueGradeIds = Array.from(new Set(gradeIds.filter(Boolean)));
  if (!workspaceId || uniqueGradeIds.length === 0) return {};
  if (options?.cacheOnly) {
    const out: Record<string, ClassMemberGradeLayout> = {};
    uniqueGradeIds.forEach((gradeId) => {
      out[gradeId] =
        loadClassMemberGradeLayoutFromStorage(workspaceId, gradeId) ?? emptyGradeLayout();
    });
    if (process.env.NODE_ENV === 'development') {
      recordDevLocalStorageProgramCacheUse('학급레이아웃맵·LS전용');
    }
    return out;
  }
  if (
    !options?.forceRefresh &&
    uniqueGradeIds.every(
      (gradeId) =>
        hasClassMemberLocalStorageSnapshot(workspaceId, gradeId) &&
        loadClassMemberGradeLayoutFromStorage(workspaceId, gradeId) != null
    )
  ) {
    const out: Record<string, ClassMemberGradeLayout> = {};
    uniqueGradeIds.forEach((gradeId) => {
      out[gradeId] = loadClassMemberGradeLayoutFromStorage(workspaceId, gradeId) ?? emptyGradeLayout();
    });
    if (process.env.NODE_ENV === 'development') {
      recordDevLocalStorageProgramCacheUse('학급레이아웃맵·LS');
    }
    return out;
  }
  const out: Record<string, ClassMemberGradeLayout> = {};
  await Promise.all(
    uniqueGradeIds.map(async (gradeId) => {
      try {
        const layout = await loadGradeLayoutCached(workspaceId, gradeId);
        out[gradeId] = layout;
      } catch {
        out[gradeId] = loadClassMemberGradeLayoutFromStorage(workspaceId, gradeId) ?? emptyGradeLayout();
      }
    })
  );
  return out;
}

export async function replaceGradeMemberLayout(
  workspaceId: string,
  gradeId: string,
  layout: ClassMemberGradeLayout
): Promise<void> {
  if (!workspaceId || !gradeId) return;

  if (layout.groups.length === 0) {
    const { data: allMembers } = await supabase
      .from('class_members')
      .select('id')
      .eq('workspace_id', workspaceId)
      .eq('grade_id', gradeId);
    const allIds = ((allMembers ?? []) as Array<{ id: string }>).map((r) => r.id);
    if (allIds.length > 0) {
      await purgeMemberOwnedSoftRefs(workspaceId, gradeId, allIds);
    }
    const { error: delM } = await supabase.from('class_members').delete().eq('workspace_id', workspaceId).eq('grade_id', gradeId);
    if (delM) throw new Error(delM.message);
    const { error: delG } = await supabase
      .from('class_member_groups')
      .delete()
      .eq('workspace_id', workspaceId)
      .eq('grade_id', gradeId);
    if (delG) throw new Error(delG.message);
    cacheClassMemberGradeLayout(workspaceId, gradeId, emptyGradeLayout());
    await invalidateSchoolScopedDataCaches(workspaceId, ['classMembers']);
    return;
  }

  const groupRows = layout.groups.map((g, i) => ({
    id: g.id.trim(),
    workspace_id: workspaceId,
    grade_id: gradeId,
    name: (g.name ?? '').trim(),
    sort_order: g.sortOrder ?? i,
    updated_at: new Date().toISOString(),
  }));
  const keepGroupIds = new Set(groupRows.map((r) => r.id));

  // 레이아웃 LS/UI에 photo 필드가 빠진 채 저장되면 upsert가 DB의 photo_profile_* 를 null로 덮어쓴다.
  // 의도적 삭제는 updateMemberPhotoProfile(직접 update)로만 하므로, 여기선 빈 값이면 기존 DB 값을 보존한다.
  const { data: existingPhotoRows, error: epErr } = await supabase
    .from('class_members')
    .select('id, photo_profile_path, photo_profile_version')
    .eq('workspace_id', workspaceId)
    .eq('grade_id', gradeId);
  if (epErr) throw new Error(epErr.message);
  const existingPhotoById = new Map(
    (
      (existingPhotoRows ?? []) as Array<{
        id: string;
        photo_profile_path?: string | null;
        photo_profile_version?: number | null;
      }>
    ).map((r) => [r.id, r] as const)
  );

  const memberPayload: Array<{
    id: string;
    workspace_id: string;
    grade_id: string;
    member_group_id: string;
    name: string;
    gender: string;
    sort_order: number;
    seat_position: { row: number; col: number } | null;
    profile_image: string;
    photo_profile_path: string | null;
    photo_profile_url: string | null;
    photo_profile_version: number | null;
    entry_number: string;
    group_no: number | null;
    /** 마이그레이션 전 RPC는 모르는 키를 무시하므로 항상 보내도 안전 */
    student_number: number | null;
    updated_at: string;
  }> = [];
  for (const g of layout.groups) {
    const list = layout.membersByGroupId[g.id] ?? [];
    list.forEach((row, index) => {
      const seat = normalizeSeatPosition(row.seatPosition);
      const memberId = row.id.trim();
      const photoPathLocal = (row.photoProfilePath ?? '').trim();
      const existing = existingPhotoById.get(memberId);
      const photoPathExisting = (existing?.photo_profile_path ?? '').trim();
      const photoPath = photoPathLocal || photoPathExisting || '';
      const photoVerLocal = Number(row.photoProfileVersion);
      const photoVerExisting = Number(existing?.photo_profile_version);
      const photoVer =
        Number.isFinite(photoVerLocal) && photoVerLocal > 0
          ? Math.trunc(photoVerLocal)
          : Number.isFinite(photoVerExisting) && photoVerExisting > 0
            ? Math.trunc(photoVerExisting)
            : null;
      memberPayload.push({
        id: memberId,
        workspace_id: workspaceId,
        grade_id: gradeId,
        member_group_id: g.id,
        name: row.name ?? '',
        gender: row.gender === '남' || row.gender === '여' ? row.gender : '',
        sort_order: index,
        seat_position: seat,
        profile_image: normalizeProfileImageId(row.profileImage) || DEFAULT_MEMBER_PROFILE_IMAGE,
        photo_profile_path: photoPath || null,
        // 비공개 버킷 전환 후 signed URL을 매번 새로 발급하므로 photo_profile_url은 저장하지 않는다.
        photo_profile_url: null,
        photo_profile_version: photoPath ? photoVer : null,
        entry_number: normalizeEntryNumber(row.entryNumber),
        group_no: normalizeMemberGroupNo(row.groupNo),
        student_number: normalizeMemberStudentNumber(row.studentNumber),
        updated_at: new Date().toISOString(),
      });
    });
  }
  const keepMemberIds = new Set(memberPayload.map((m) => m.id));

  const { data: existingGroups, error: egErr } = await supabase
    .from('class_member_groups')
    .select('id')
    .eq('workspace_id', workspaceId)
    .eq('grade_id', gradeId);
  if (egErr) throw new Error(egErr.message);
  const deleteGroupIds = ((existingGroups ?? []) as Array<{ id: string }>)
    .map((r) => r.id)
    .filter((id) => !keepGroupIds.has(id));

  const { error: upsertG } = await supabase.from('class_member_groups').upsert(groupRows, { onConflict: 'id' });
  if (upsertG) throw new Error(upsertG.message);

  if (memberPayload.length > 0) {
    // class_members_upsert(SECURITY DEFINER)가 name을 pgp_sym_encrypt로 암호화해 name_enc에 저장한다.
    // 현재 스키마는 이미 photo_profile_*/seat_position/profile_image 컬럼을 모두 포함하므로
    // 과거의 컬럼 존재 여부 폴백은 더 이상 필요 없다.
    const { error: upsertM } = await supabase.rpc('class_members_upsert', { p_rows: memberPayload });
    if (upsertM) throw new Error(upsertM.message);
  }

  const { data: existingMembers, error: emErr } = await supabase
    .from('class_members')
    .select('id')
    .eq('workspace_id', workspaceId)
    .eq('grade_id', gradeId);
  if (emErr) throw new Error(emErr.message);
  const removeMemberIds = ((existingMembers ?? []) as Array<{ id: string }>)
    .map((r) => r.id)
    .filter((id) => !keepMemberIds.has(id));
  if (removeMemberIds.length > 0) {
    await purgeMemberOwnedSoftRefs(workspaceId, gradeId, removeMemberIds);
    const { error: delM } = await supabase.from('class_members').delete().in('id', removeMemberIds);
    if (delM) throw new Error(delM.message);
  }

  if (memberPayload.length === 0) {
    const { error: delAllM } = await supabase.from('class_members').delete().eq('workspace_id', workspaceId).eq('grade_id', gradeId);
    if (delAllM) throw new Error(delAllM.message);
  }

  if (deleteGroupIds.length > 0) {
    const { error: delG } = await supabase.from('class_member_groups').delete().in('id', deleteGroupIds);
    if (delG) throw new Error(delG.message);
  }

  // upsert 시 보존한 photo 필드를 LS 레이아웃에도 반영 (다음 저장·표시에서 path가 빠지지 않게)
  const photoResolvedById = new Map(
    memberPayload.map((m) => [
      m.id,
      {
        path: m.photo_profile_path,
        version: m.photo_profile_version,
      },
    ] as const)
  );
  const membersByGroupIdCached: Record<string, ClassMemberRow[]> = {};
  for (const g of layout.groups) {
    membersByGroupIdCached[g.id] = (layout.membersByGroupId[g.id] ?? []).map((row) => {
      const resolved = photoResolvedById.get(row.id.trim());
      if (!resolved?.path) {
        const next = { ...row };
        delete next.photoProfilePath;
        delete next.photoProfileVersion;
        delete next.photoProfileUrl;
        return next;
      }
      return {
        ...row,
        photoProfilePath: resolved.path,
        ...(resolved.version ? { photoProfileVersion: resolved.version } : {}),
      };
    });
  }
  await invalidateSchoolScopedDataCaches(workspaceId, ['classMembers']);
  allowSchoolScopedCacheSeedAfterInvalidate(workspaceId);
  cacheClassMemberGradeLayout(workspaceId, gradeId, {
    ...layout,
    membersByGroupId: membersByGroupIdCached,
  });
}

/** 멤버만 넘기는 기존 API: 학년당 하나의 그룹으로 통합 후 저장 */
export async function replaceClassMembers(
  workspaceId: string,
  gradeId: string,
  rows: ClassMemberRow[]
): Promise<ClassMemberRow[]> {
  if (!workspaceId || !gradeId) return [];
  const groups = await fetchGroupsForGrade(workspaceId, gradeId);
  let keepId: string;
  let keepName: string;
  if (groups.length === 0) {
    keepId = await createDefaultGroupRow(workspaceId, gradeId);
    keepName = '기본';
  } else {
    keepId = groups[0].id;
    keepName = groups[0].name ?? '기본';
  }
  const deleteOtherIds = groups.map((g) => g.id).filter((id) => id !== keepId);
  if (deleteOtherIds.length > 0) {
    const { error } = await supabase.from('class_member_groups').delete().in('id', deleteOtherIds);
    if (error) throw new Error(error.message);
  }
  const layout: ClassMemberGradeLayout = {
    groups: [{ id: keepId, name: keepName, sortOrder: 0 }],
    membersByGroupId: { [keepId]: rows },
  };
  await replaceGradeMemberLayout(workspaceId, gradeId, layout);
  return rows;
}

export type GetClassMembersOptions = { forceRefresh?: boolean };

export async function getClassMembers(
  workspaceId: string,
  gradeId: string,
  options?: GetClassMembersOptions
): Promise<ClassMemberRow[]> {
  if (!workspaceId || !gradeId) return [];
  if (
    !options?.forceRefresh &&
    hasClassMemberLocalStorageSnapshot(workspaceId, gradeId)
  ) {
    const lay = loadClassMemberGradeLayoutFromStorage(workspaceId, gradeId);
    if (lay != null) {
      if (process.env.NODE_ENV === 'development') {
        recordDevLocalStorageProgramCacheUse('학급멤버·LS');
      }
      return flattenGradeLayout(lay);
    }
  }
  const cachedFlat = loadClassMembers(workspaceId, gradeId);
  let layout: ClassMemberGradeLayout;
  try {
    layout = await loadGradeLayoutCached(workspaceId, gradeId);
  } catch {
    layout = loadClassMemberGradeLayoutFromStorage(workspaceId, gradeId) ?? emptyGradeLayout();
    const flat = flattenGradeLayout(layout);
    if (flat.length === 0 && cachedFlat.length > 0) {
      await replaceClassMembers(workspaceId, gradeId, cachedFlat);
      return cachedFlat;
    }
    return flat;
  }
  const rows = flattenGradeLayout(layout);
  if (rows.length === 0 && cachedFlat.length > 0) {
    await replaceClassMembers(workspaceId, gradeId, cachedFlat);
    return cachedFlat;
  }
  return rows;
}

export async function getClassMembersMap(
  workspaceId: string,
  gradeIds: string[]
): Promise<Record<string, ClassMemberRow[]>> {
  const uniqueGradeIds = Array.from(new Set(gradeIds.filter(Boolean)));
  if (!workspaceId || uniqueGradeIds.length === 0) return {};
  const layouts = await getClassMemberGradeLayoutsMap(workspaceId, uniqueGradeIds);
  const out: Record<string, ClassMemberRow[]> = {};
  for (const gradeId of uniqueGradeIds) {
    let layout = layouts[gradeId] ?? emptyGradeLayout();
    let rows = flattenGradeLayout(layout);
    if (rows.length === 0) {
      const cachedFlat = loadClassMembers(workspaceId, gradeId);
      if (cachedFlat.length > 0) {
        await replaceClassMembers(workspaceId, gradeId, cachedFlat);
        rows = cachedFlat;
        layout = await loadGradeLayoutCached(workspaceId, gradeId);
      }
    }
    out[gradeId] = rows;
    cacheClassMemberGradeLayout(workspaceId, gradeId, layout);
  }
  return out;
}

export function getCachedClassMembers(workspaceId: string, gradeId: string): ClassMemberRow[] {
  return loadClassMembers(workspaceId, gradeId);
}

/** 멤버 사진 프로필 필드만 DB·로컬 캐시에 반영 */
export async function updateMemberPhotoProfile(params: {
  workspaceId: string;
  gradeId: string;
  memberId: string;
  photoProfilePath?: string;
  photoProfileVersion?: number;
}): Promise<void> {
  const { workspaceId, gradeId, memberId } = params;
  if (!workspaceId || !gradeId || !memberId) return;

  const path = (params.photoProfilePath ?? '').trim() || null;
  const verRaw = Number(params.photoProfileVersion);
  const version = path && Number.isFinite(verRaw) && verRaw > 0 ? Math.trunc(verRaw) : null;

  // photo_profile_url(과거 public URL) 컬럼은 비공개 버킷 전환 후 더 이상 신뢰하지 않으므로 항상 비운다.
  const { error } = await supabase
    .from('class_members')
    .update({
      photo_profile_path: path,
      photo_profile_url: null,
      photo_profile_version: version,
      updated_at: new Date().toISOString(),
    })
    .eq('workspace_id', workspaceId)
    .eq('grade_id', gradeId)
    .eq('id', memberId);
  if (error) throw new Error(error.message);

  const layout =
    loadClassMemberGradeLayoutFromStorage(workspaceId, gradeId) ?? emptyGradeLayout();
  let changed = false;
  const membersByGroupId: Record<string, ClassMemberRow[]> = {};
  for (const [gid, list] of Object.entries(layout.membersByGroupId)) {
    membersByGroupId[gid] = list.map((m) => {
      if (m.id !== memberId) return m;
      changed = true;
      const next: ClassMemberRow = { ...m };
      delete next.photoProfileUrl;
      if (path) {
        next.photoProfilePath = path;
        if (version) next.photoProfileVersion = version;
        else delete next.photoProfileVersion;
      } else {
        delete next.photoProfilePath;
        delete next.photoProfileVersion;
      }
      return next;
    });
  }
  if (changed) {
    cacheClassMemberGradeLayout(workspaceId, gradeId, {
      ...layout,
      membersByGroupId,
    });
  }
  // 사진 path만 갱신하므로 전역 school LS purge는 하지 않는다.
  // (purge 직후 photo 없는 스냅샷이 다시 쓰이면, 이후 멤버 일괄 저장이 DB path를 지울 수 있었음)
  await notifySchoolProgramMutation(workspaceId, ['classMembers']);
}

export type { ClassMemberGradeLayout, ClassMemberGroupMeta } from '../utils/classMembersStorage';
